/**
 * Spec 027: the sheets of an agent's card — a new agent, the setup, the settings a person
 * changes by hand, and approving the version their own computer runs (D1, D4).
 *
 * Every save is a PATCH with the version read; a 409 means someone changed the card
 * meanwhile, and the card reloads to show it as it is now.
 */

import { useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, newIdempotencyKey } from "@/api/client";
import { Agents, Projects, ResourceCards, Secrets } from "@/api/resources";
import type { AccountApp } from "@/api/accounts";
import type { RadarRight } from "@/api/radar";
import { CompanyTools, type FoundNote, type FoundSkill } from "@/api/units";
import type { AgentAction, AgentDetailOut, AgentDiffOut, AgentGrantedNote, AgentNoteGrant, AgentPatch, AgentResultMode, AgentRights, AgentServiceLimit, AgentWebTool, ClaudeEffort, CodexEffort, HelperMode, KitExclude } from "@/api/types";
import { AgentSettingsSummary } from "@/components/AgentSettingsSummary";
import { CARD_VERDICTS, NoteSearch, SkillSearch, verdictNote } from "@/components/GrantSearch";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Segmented } from "@/components/ui/Segmented";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { Caption, EmptyNote, Switch } from "@/kit";
import { ANDROID_APPS, APP_LABEL } from "@/lib/accounts";
import { ACTION_LABEL, ACTIONS, approvable, limitOf, markSplit, MODE_LABEL, partLabel } from "@/lib/agents";
import { cn } from "@/lib/cn";
import { INBOX_PROJECT_ID } from "@/lib/limits";

const refused = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408;

export function useAgentSave(agent: AgentDetailOut, onDone: () => void, done: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (patch: AgentPatch) => Agents.update(agent.id, { ...patch, version: agent.version }),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ["agents"] });
      toast.success(saved.version > agent.version ? `${done} — версия ${saved.version}` : done);
      onDone();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["agents"] });
    },
  });
}

/** A person creates an agent: a name and what it is for; the card starts as version 1. */
export function NewAgentSheet({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: AgentDetailOut) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  // A resend after a lost reply reuses the key and gets the first agent back.
  const attempt = useRef<string | null>(null);

  const save = async () => {
    setError(undefined);
    setSaving(true);
    const key = (attempt.current ??= newIdempotencyKey());
    try {
      const created = await Agents.create({ name: name.trim(), purpose_md: purpose.trim() || null }, key);
      attempt.current = null;
      setName("");
      setPurpose("");
      void qc.invalidateQueries({ queryKey: ["agents"] });
      toast.success(`Сотрудник «${created.name}» создан — версия 1`);
      onCreated(created);
    } catch (e) {
      if (refused(e)) attempt.current = null;
      setError(refused(e) ? e.message : "Ответ сервера не дошёл. Нажми «Создать» ещё раз — второй агент не появится.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Новый сотрудник"
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Создаём…" disabled={!name.trim()} onClick={() => void save()}>
            Создать
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField label="Имя" hint="Так его зовут в разделе и в команде запуска" value={name} maxLength={120}
          onChange={(e) => { attempt.current = null; setName(e.target.value); }} />
        <TextArea label="Для чего" hint="Одной-двумя фразами" value={purpose} rows={3} maxLength={4000}
          onChange={(e) => { attempt.current = null; setPurpose(e.target.value); }} />
        <p className="text-meta text-p-text-muted">
          Установку, схему, помощников и навыки допишешь в карточке. Права и лимиты задаются там же, руками.
        </p>
      </div>
    </Sheet>
  );
}

/**
 * Spec 042: a person adds a helper to a staff member — a card of its own under it, in the
 * staff member's unit. By default it works in the staff member's chat; a chat of its own is
 * for another computer, model or rights.
 */
export function NewHelperSheet({ parent, onClose, onCreated }: {
  parent: AgentDetailOut;
  onClose: () => void;
  onCreated: (created: AgentDetailOut) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [mode, setMode] = useState<HelperMode>("background");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const attempt = useRef<string | null>(null);

  const save = async () => {
    setError(undefined);
    setSaving(true);
    const key = (attempt.current ??= newIdempotencyKey());
    try {
      const created = await Agents.create(
        { name: name.trim(), purpose_md: purpose.trim() || null, parent_id: parent.id, helper_mode: mode },
        key,
      );
      attempt.current = null;
      void qc.invalidateQueries({ queryKey: ["agents"] });
      void qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success(`Помощник «${created.name}» добавлен — версия 1`);
      onCreated(created);
    } catch (e) {
      if (refused(e)) attempt.current = null;
      setError(refused(e) ? e.message : "Ответ сервера не дошёл. Нажми «Добавить» ещё раз — второй помощник не появится.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Помощник сотрудника «${parent.name}»`}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Добавляем…" disabled={!name.trim()} onClick={() => void save()}>
            Добавить
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField label="Имя" hint="Так его называют шаги схемы сотрудника" value={name} maxLength={120}
          onChange={(e) => { attempt.current = null; setName(e.target.value); }} />
        <TextArea label="За что отвечает" hint="Одной-двумя фразами" value={purpose} rows={3} maxLength={4000}
          onChange={(e) => { attempt.current = null; setPurpose(e.target.value); }} />
        <Segmented options={HELPER_MODES} value={mode} onChange={setMode} />
        <p className="text-meta text-p-text-muted">
          {mode === "own_chat"
            ? "Свой чат — когда нужен другой компьютер, модель или права. Его запускают из его карточки, пока идёт сотрудник."
            : "В чате сотрудника: сотрудник запускает его сам, с его ключом задачника; вопросы помощника получает сотрудник."}
        </p>
        <p className="text-meta text-p-text-muted">
          Установку, навыки, права и лимиты помощника допишешь в его карточке. Он ходит за сотрудником в его отдел; одна кнопка «Одобрить» у сотрудника одобряет и его помощников.
        </p>
      </div>
    </Sheet>
  );
}

/** The instructions the agent works by; the name and the purpose sit next to them. */
export function SetupSheet({ agent, onClose }: { agent: AgentDetailOut; onClose: () => void }) {
  const [name, setName] = useState(agent.name);
  const [purpose, setPurpose] = useState(agent.purpose_md ?? "");
  const [setup, setSetup] = useState(agent.card.setup_md);
  const save = useAgentSave(agent, onClose, "Карточка сохранена");
  const patch: AgentPatch = {};
  if (name.trim() && name.trim() !== agent.name) patch.name = name.trim();
  if ((purpose.trim() || null) !== (agent.purpose_md ?? null)) patch.purpose_md = purpose.trim() || null;
  if (setup !== agent.card.setup_md) patch.setup_md = setup;
  return (
    <Sheet open onClose={onClose} title="Установка агента"
      footer={<Button block variant="strong" loading={save.isPending} disabled={!Object.keys(patch).length} onClick={() => save.mutate(patch)}>Сохранить</Button>}>
      <div className="flex flex-col gap-3">
        <TextField label="Имя" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        <TextArea label="Для чего" value={purpose} rows={2} maxLength={4000} onChange={(e) => setPurpose(e.target.value)} />
        <TextArea label="Установка" hint="Инструкции, по которым агент работает. Изменение — новая версия, её одобряет каждый для своего компьютера."
          value={setup} rows={12} maxLength={50_000} onChange={(e) => setSetup(e.target.value)} />
      </div>
    </Sheet>
  );
}

/** Spec 038: company tools by key; each needs a limit of the same name. Spec 048: «Находки»
 * (`radar` in the card; step 2 — a base of «Знания», not of a unit).
 * Spec 049/050: the PC's own emulator and speech to text, in minutes a day; step 4 — follows in
 * the emulator's apps, a limit «follow» a day per account.
 * Spec 052/054: the clients' orders it reaches, their sums, and writing to the client in the chat
 * of an order it leads. */
type DeviceRight = "android" | "whisper";
type OrdersRight = "none" | "own" | "all";
type FinanceRight = "none" | "read";
/** Spec 054, step 3b: the passwords and keys a client gave — by an open unit order of its order. */
type ClientSecretsRight = "none" | "by_order";
type WithTools = AgentRights & { tools?: string[]; radar?: RadarRight; device?: DeviceRight[]; android_apps?: AccountApp[]; android_follow?: boolean; orders?: OrdersRight; finance?: FinanceRight; client_chat?: boolean; client_secrets?: ClientSecretsRight; contract_review?: boolean };
const FOLLOW_MAX = 30;

const DEVICE: { right: DeviceRight; label: string }[] = [
  { right: "android", label: "Эмулятор Android — свой, на ПК, где запущен" },
  { right: "whisper", label: "Расшифровка звука на ПК (Whisper)" },
];

const RADAR_LEVELS: { value: RadarRight; label: string }[] = [
  { value: "none", label: "нет" },
  { value: "read", label: "читать" },
  { value: "report", label: "сообщать" },
  { value: "edit", label: "вести" },
];

const ORDER_LEVELS: { value: OrdersRight; label: string }[] = [
  { value: "none", label: "нет" },
  { value: "own", label: "свои" },
  { value: "all", label: "все" },
];

const FINANCE_LEVELS: { value: FinanceRight; label: string }[] = [
  { value: "none", label: "не видит" },
  { value: "read", label: "видит суммы" },
];

const CLIENT_SECRETS_LEVELS: { value: ClientSecretsRight; label: string }[] = [
  { value: "none", label: "нет" },
  { value: "by_order", label: "по поручению заказа" },
];

const MODES = (Object.keys(MODE_LABEL) as AgentResultMode[]).map((value) => ({ value, label: value === "wait" ? "Ждать «да»" : "Параллельно", hint: MODE_LABEL[value] }));

/** Spec 042: where a helper works — the staff member's chat, or a chat of its own. */
const HELPER_MODES: { value: HelperMode; label: string }[] = [
  { value: "background", label: "В чате сотрудника" },
  { value: "own_chat", label: "В своём чате" },
];

/**
 * The result mode, confirmations, rights, limits and the caution skills ticked on purpose:
 * by hand here, or by the person's own agent on their words (Spec 036); never in a run.
 * Spec 055, step 3: the sheet opens with the summary — to read and check; «Изменить» opens the
 * editing. Notes and skills list only what is granted, «+ Добавить» finds more by name, and
 * what the unit's kit gives may be taken away for this agent (`kit_exclude`).
 */
export function SettingsSheet({ agent, onClose, editing: startEditing = false }: {
  agent: AgentDetailOut;
  onClose: () => void;
  /** Open the editing at once, past the summary. */
  editing?: boolean;
}) {
  const card = agent.card;
  const summary = agent.settings ?? null;
  const kit = summary?.from_unit?.kit;
  const exclude = card.kit_exclude ?? NO_EXCLUDE;
  const [editing, setEditing] = useState(startEditing);
  const [changes, setChanges] = useState(false);
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list(), enabled: editing });
  const [mode, setMode] = useState<AgentResultMode>(card.result_mode);
  const [confirmations, setConfirmations] = useState(card.confirmations.join("\n"));
  const [allProjects, setAllProjects] = useState(card.rights.all_projects);
  const [chosen, setChosen] = useState<string[]>(card.rights.projects);
  const [actions, setActions] = useState<AgentAction[]>(card.rights.actions);
  const [readAll, setReadAll] = useState(card.rights.read_all);
  // Notes by address keep their grants even when the list below does not show them (review: a save
  // must never drop a grant it did not touch).
  const [noteGrants, setNoteGrants] = useState<AgentNoteGrant[]>(card.rights.notes ?? []);
  const [noteTitles, setNoteTitles] = useState<Record<string, string>>(() =>
    Object.fromEntries((summary?.card_notes ?? []).map((n) => [n.note_id, n.title])));
  const [exNotes, setExNotes] = useState<string[]>(exclude.notes);
  const [web, setWeb] = useState<AgentWebTool[]>(card.rights.web ?? []);
  // Spec 038: the company's tools by key; a save keeps them like every other right.
  const [tools, setTools] = useState<string[]>((card.rights as WithTools).tools ?? []);
  const companyTools = useQuery({ queryKey: ["company", "tools"], queryFn: () => CompanyTools.list(), enabled: editing });
  const [radar, setRadar] = useState<RadarRight>((card.rights as WithTools).radar ?? "none");
  const [device, setDevice] = useState<DeviceRight[]>((card.rights as WithTools).device ?? []);
  const [apps, setApps] = useState<AccountApp[]>((card.rights as WithTools).android_apps ?? []);
  const [follow, setFollow] = useState<boolean>((card.rights as WithTools).android_follow ?? false);
  // Spec 052/054 (task 5ba33510): these were not on the sheet, and a save reset them to none.
  const [orders, setOrders] = useState<OrdersRight>((card.rights as WithTools).orders ?? "none");
  const [finance, setFinance] = useState<FinanceRight>((card.rights as WithTools).finance ?? "none");
  const [clientChat, setClientChat] = useState<boolean>((card.rights as WithTools).client_chat ?? false);
  const [clientSecrets, setClientSecrets] = useState<ClientSecretsRight>((card.rights as WithTools).client_secrets ?? "none");
  // Spec 061: «Юрист» approves contracts and licences of the orders it reaches.
  const [contractReview, setContractReview] = useState<boolean>((card.rights as WithTools).contract_review ?? false);
  const [iterations, setIterations] = useState(String(card.limits.iterations_per_run ?? ""));
  const [perRun, setPerRun] = useState(String(card.limits.tokens_per_run ?? ""));
  const [perDay, setPerDay] = useState(String(card.limits.tokens_per_day ?? ""));
  const [services, setServices] = useState<AgentServiceLimit[]>(card.limits.services);
  const [ownSkills, setOwnSkills] = useState<string[]>(card.skills);
  const [ticked, setTicked] = useState<string[]>(card.skills_ticked);
  const [skillVerdicts, setSkillVerdicts] = useState<Record<string, FoundSkill["verdict"]>>(() =>
    Object.fromEntries((summary?.card_skills ?? []).map((s) => [s.slug, s.verdict])));
  const [exSkills, setExSkills] = useState<string[]>(exclude.skills);
  // Spec 042: the chat's model, and for a helper where it works. Spec 055: each runtime's effort.
  const [model, setModel] = useState(card.model ?? "");
  const [codexModel, setCodexModel] = useState(card.codex_model ?? "");
  const [effort, setEffort] = useState<ClaudeEffort | "">(card.effort ?? "");
  const [codexEffort, setCodexEffort] = useState<CodexEffort | "">(card.codex_reasoning_effort ?? "");
  const [helperMode, setHelperMode] = useState<HelperMode>(agent.helper_mode ?? "background");
  const save = useAgentSave(agent, onClose, "Настройки сохранены");

  const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  const own = confirmations.split("\n").map((s) => s.trim()).filter(Boolean);
  // Step 4: follows go with their limit «follow» — at most FOLLOW_MAX a day per account.
  const followLimit = services.find((s) => s.name.trim().toLowerCase() === "follow");
  const followNote = !follow
    ? "Только кнопка «Follow» / «Подписаться»: таскер считает подписки каждого аккаунта, не чаще раза в 5 минут"
    : !followLimit || followLimit.per_day < 1
      ? "нужен лимит «follow» ниже — подписок в сутки на аккаунт"
      : followLimit.per_day > FOLLOW_MAX
        ? `не больше ${FOLLOW_MAX} в сутки на аккаунт — больше рискует баном`
        : `до ${followLimit.per_day} в сутки на аккаунт, раз в 5 минут; «Action blocked» — пауза 72 ч`;
  const patch: AgentPatch = {
    result_mode: mode,
    confirmations: own,
    rights: {
      // The server replaces the rights whole: a right this sheet does not show goes back as it was.
      ...card.rights,
      all_projects: allProjects, projects: allProjects ? [] : chosen, actions, read_all: readAll, notes: noteGrants, web, tools, radar,
      device, android_apps: device.includes("android") ? apps : [], android_follow: device.includes("android") && follow,
      orders, finance, client_chat: clientChat, client_secrets: clientSecrets, contract_review: contractReview,
    } as WithTools,
    limits: {
      iterations_per_run: limitOf(iterations),
      tokens_per_run: limitOf(perRun),
      tokens_per_day: limitOf(perDay),
      services: services.filter((s) => s.name.trim() && s.unit.trim() && s.per_day >= 1),
    },
    skills: ownSkills,
    skills_ticked: ticked.filter((slug) => ownSkills.includes(slug)),
    model: model.trim() || null,
    codex_model: codexModel.trim() || null,
    // An empty effort goes as "": the server clears it — back to the runtime's default.
    effort,
    codex_reasoning_effort: codexEffort,
    kit_exclude: { ...exclude, notes: exNotes, skills: exSkills },
    ...(agent.parent_id ? { helper_mode: helperMode } : {}),
  };

  if (!editing) {
    return (
      <Sheet open onClose={onClose} title="Настройки агента"
        footer={
          <div className="flex gap-2">
            <Button block variant="secondary" onClick={() => setChanges(!changes)}>Что изменилось</Button>
            <Button block variant="strong" onClick={() => setEditing(true)}>Изменить</Button>
          </div>
        }>
        <div className="flex flex-col gap-4">
          {summary ? <AgentSettingsSummary settings={summary} /> : <EmptyNote>Сводки нет — открой «Изменить».</EmptyNote>}
          {changes && <ChangesSince agent={agent} />}
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet open onClose={onClose} title="Настройки агента"
      footer={<Button block variant="strong" loading={save.isPending} onClick={() => save.mutate(patch)}>Сохранить</Button>}>
      <div className="flex flex-col gap-5">
        <p className="text-meta text-p-text-muted">Эти настройки меняет человек — здесь или через своего агента, твоими дословными словами. Агенту отдела в его запуске сервер их не даст.</p>

        <section className="space-y-2">
          <Caption>Результат</Caption>
          <Segmented options={MODES} value={mode} onChange={setMode} />
        </section>

        <section className="space-y-2">
          <Caption>Подтверждения</Caption>
          <ul className="space-y-1 text-meta text-p-text-muted">
            {agent.fixed_minimum.map((item) => <li key={item}>· всегда с «да» человека: {item}</li>)}
          </ul>
          <TextArea label="О чём ещё спрашивать" hint="По одному на строку" value={confirmations} rows={3}
            onChange={(e) => setConfirmations(e.target.value)} />
        </section>

        <section className="space-y-2">
          <Caption>Права</Caption>
          <Toggle label="Во всех проектах" on={allProjects} onChange={() => setAllProjects(!allProjects)} />
          {!allProjects && (projects.isLoading ? <SkeletonList count={2} /> : (
            <div className="flex flex-col gap-1">
              {(projects.data ?? []).filter((p) => !p.archived_at && p.id !== INBOX_PROJECT_ID).map((p) => (
                <Tick key={p.id} label={p.title} on={chosen.includes(p.id)} onChange={() => setChosen(toggle(chosen, p.id))} />
              ))}
            </div>
          ))}
          <div className="flex flex-col gap-1 pt-1">
            {ACTIONS.map((a) => <Tick key={a} label={ACTION_LABEL[a]} on={actions.includes(a)} onChange={() => setActions(toggle(actions, a))} />)}
          </div>
          <Toggle label="Читать всё пространство: поиск, обзор, общий чат" on={readAll} onChange={() => setReadAll(!readAll)} />
          <NoteGrants grants={noteGrants} titles={noteTitles} kitNotes={kit?.notes ?? []} excluded={exNotes}
            onChange={setNoteGrants}
            onPick={(note) => {
              setNoteTitles({ ...noteTitles, [note.id]: note.title });
              setNoteGrants([...noteGrants, { note_id: note.id, append: false }]);
            }}
            onExclude={(id) => setExNotes(toggle(exNotes, id))} />
          <div className="flex flex-col gap-1 pt-1">
            <p className="text-meta text-p-text-muted">Сеть — только с лимитом сервиса с тем же именем. Claude Code: папка запуска считает каждый вызов и останавливает на лимите. Codex: WebSearch открывает его поиск, вызовы считаются после и запуск останавливается на следующем шаге.</p>
            {WEB_TOOLS.map(({ tool, label }) => {
              const limited = services.some((s) => s.name.trim().toLowerCase() === tool.toLowerCase() && s.per_day >= 1);
              return (
                <Tick key={tool} label={label} on={web.includes(tool)}
                  note={web.includes(tool) && !limited ? `нужен лимит «${tool}» ниже` : undefined}
                  onChange={() => {
                    setWeb(toggle(web, tool));
                    // A right without its limit is no right (D7): the row to fill comes with the tick.
                    if (!web.includes(tool) && !services.some((s) => s.name.trim().toLowerCase() === tool.toLowerCase())) {
                      setServices([...services, { name: tool, unit: "вызов", per_day: 0 }]);
                    }
                  }} />
              );
            })}
          </div>
          <div className="flex flex-col gap-1 pt-1" data-company-tools>
            <p className="text-meta text-p-text-muted">Инструменты сотрудников — вызовы идут через задачник с его ключом; лимит — генераций в сутки, сервис с тем же именем.</p>
            {(companyTools.data ?? []).map((t) => {
              const limited = services.some((s) => s.name.trim().toLowerCase() === t.key && s.per_day >= 1);
              const on = tools.includes(t.key);
              return (
                <Tick key={t.id} label={t.title} on={on}
                  note={on && !limited ? `нужен лимит «${t.key}» ниже` : t.has_key ? t.key : `${t.key} · ключ не привязан`}
                  onChange={() => {
                    setTools(toggle(tools, t.key));
                    if (!on && !services.some((s) => s.name.trim().toLowerCase() === t.key)) {
                      setServices([...services, { name: t.key, unit: "генерация", per_day: 0 }]);
                    }
                  }} />
              );
            })}
            {tools.filter((key) => !(companyTools.data ?? []).some((t) => t.key === key)).map((key) => (
              <Tick key={key} label={`${key} — нет в «Инструментах»`} on onChange={() => setTools(tools.filter((x) => x !== key))} />
            ))}
            {companyTools.isSuccess && !(companyTools.data ?? []).length && !tools.length && (
              <span className="text-meta text-p-text-muted">Инструментов пока нет: их заводят в «Отделах» → «Инструменты».</span>
            )}
          </div>
          <div className="flex flex-col gap-2 pt-1" data-radar-right>
            <p className="text-meta text-p-text-muted">Находки — база в «Знаниях», что сотрудники принесли из интернета: читать — искать в ней; сообщать — находки, упоминания, релизы, применения; вести — ещё кольца, доверие, дубли и отклонение.</p>
            <Segmented options={RADAR_LEVELS} value={radar} onChange={setRadar} label="Находки" />
          </div>
          <div className="flex flex-col gap-2 pt-1" data-orders-rights>
            <p className="text-meta text-p-text-muted">Заказы клиентов — свои: те, что сотрудник ведёт менеджером или делает по ним поручение отдела; все — любые. Читает их и готовит черновики документов. Суммы — отдельно: без них он видит заказы без денег.</p>
            <Segmented options={ORDER_LEVELS} value={orders} onChange={setOrders} label="Заказы клиентов" />
            <Segmented options={FINANCE_LEVELS} value={finance} onChange={setFinance} label="Суммы заказов" />
            <Toggle label="Чат с клиентом — пишет клиенту в чате заказа, который ведёт, без «да» на каждое сообщение" on={clientChat} onChange={() => setClientChat(!clientChat)} />
            <p className="text-meta text-p-text-muted">Цена, КП и допсоглашение уходят клиенту только документом после «да» человека; договор — после «да» «Юриста» или человека.</p>
            <Toggle label="Проверка договоров — утверждает и отклоняет договоры и лицензии заказов; сумма договора — из КП, утверждённого людьми" on={contractReview} onChange={() => setContractReview(!contractReview)} />
            <Segmented options={CLIENT_SECRETS_LEVELS} value={clientSecrets} onChange={setClientSecrets} label="Доступы клиентов" />
            <p className="text-meta text-p-text-muted">Доступы клиентов — пароли и ключи, которые клиент вписал на странице таскера. По поручению заказа: сотрудник берёт их файлом на 60 секунд, только пока делает открытое поручение открытого заказа этого клиента; каждый раз — в журнале секрета.</p>
          </div>
          <div className="flex flex-col gap-1 pt-1" data-device-rights>
            <p className="text-meta text-p-text-muted">Устройство ПК — то, что даёт приложение «Агенты» там, где сотрудник запущен. Лимит — минут в сутки, сервис с тем же именем. Аккаунты для входа — в блоке «Аккаунты» сотрудника; пароли агент не видит.</p>
            {DEVICE.map(({ right, label }) => {
              const on = device.includes(right);
              const limited = services.some((s) => s.name.trim().toLowerCase() === right && s.per_day >= 1);
              return (
                <Tick key={right} label={label} on={on}
                  note={on && !limited ? `нужен лимит «${right}» ниже` : undefined}
                  onChange={() => {
                    setDevice(toggle(device, right));
                    if (!on && !services.some((s) => s.name.trim().toLowerCase() === right)) {
                      setServices([...services, { name: right, unit: "минута", per_day: 0 }]);
                    }
                  }} />
              );
            })}
            {device.includes("android") && (
              <div className="flex flex-col gap-1 pl-6" data-android-apps>
                <span className="text-meta text-p-text-muted">Какие приложения открывает: смотрит и ищет — писать, лайкать и комментировать приложение «Агенты» не даст.</span>
                {ANDROID_APPS.map((app) => (
                  <Tick key={app} label={APP_LABEL[app]} on={apps.includes(app)} onChange={() => setApps(toggle(apps, app))} />
                ))}
                <Tick label="Подписки на авторов" on={follow} note={followNote}
                  onChange={() => {
                    setFollow(!follow);
                    if (!follow && !followLimit) setServices([...services, { name: "follow", unit: "подписка", per_day: 0 }]);
                  }} />
              </div>
            )}
          </div>
        </section>

        <section className="space-y-2">
          <Caption>Лимиты</Caption>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <TextField label="Итераций за запуск" inputMode="numeric" value={iterations} placeholder="без лимита" onChange={(e) => setIterations(e.target.value)} />
            <TextField label="Токенов за запуск" inputMode="numeric" value={perRun} placeholder="без лимита" onChange={(e) => setPerRun(e.target.value)} />
            <TextField label="Токенов в сутки" inputMode="numeric" value={perDay} placeholder="без лимита" onChange={(e) => setPerDay(e.target.value)} />
          </div>
          <p className="text-meta text-p-text-muted">Лимит внешнего сервиса — твоё «да» тратить до него. Платный сервис без лимита агент не использует.</p>
          {services.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2">
              <TextField label="Сервис" value={s.name} onChange={(e) => setServices(services.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <TextField label="Единица" value={s.unit} onChange={(e) => setServices(services.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)))} />
              <TextField label="В сутки" inputMode="numeric" value={String(s.per_day || "")} onChange={(e) => setServices(services.map((x, j) => (j === i ? { ...x, per_day: limitOf(e.target.value) ?? 0 } : x)))} />
              <SecretPicker value={s.secret_id ?? null} onChange={(secret_id) => setServices(services.map((x, j) => (j === i ? { ...x, secret_id } : x)))} />
              <Button size="compact" variant="ghost" onClick={() => setServices(services.filter((_, j) => j !== i))}>Убрать</Button>
            </div>
          ))}
          <Button size="compact" variant="secondary" onClick={() => setServices([...services, { name: "", unit: "", per_day: 0 }])}>Лимит сервиса</Button>
        </section>

        <section className="space-y-2">
          <Caption>Модель</Caption>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <TextField label="Claude Code" hint="Например sonnet или opus; пусто — по умолчанию" value={model} maxLength={64} onChange={(e) => setModel(e.target.value)} />
            <TextField label="Codex" hint="Имя модели, доступной в твоём Codex; пусто — по умолчанию. Модели Claude сюда не переносятся." value={codexModel} maxLength={64} onChange={(e) => setCodexModel(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2" data-efforts>
            <Segmented options={CLAUDE_EFFORTS} value={effort} onChange={setEffort} label="Effort Claude Code" />
            <Segmented options={CODEX_EFFORTS} value={codexEffort} onChange={setCodexEffort} label="Effort Codex" />
            <p className="text-meta text-p-text-muted">У каждой нейросети свой effort: Claude Code — флаг --effort, Codex — model_reasoning_effort (xhigh есть не у всех моделей). Значение одной в другую не переносится.</p>
          </div>
        </section>

        {agent.parent_id && (
          <section className="space-y-2" data-helper-mode>
            <Caption>Где работает помощник</Caption>
            <Segmented options={HELPER_MODES} value={helperMode} onChange={setHelperMode} />
            <p className="text-meta text-p-text-muted">
              {helperMode === "own_chat"
                ? "Свой чат — когда нужен другой компьютер, модель или права. Его запускают из этой карточки, пока идёт сотрудник; шаги он делает через prosto.task."
                : "В чате сотрудника: сотрудник запускает его сам, файл помощника лежит в папке запуска сотрудника. Смена режима — без новой версии."}
            </p>
          </section>
        )}

        <section className="space-y-2">
          <Caption>Навыки</Caption>
          <SkillGrants own={ownSkills} verdicts={skillVerdicts} ticked={ticked} kitSkills={kit?.skills ?? []} excluded={exSkills}
            onRemove={(slug) => setOwnSkills(ownSkills.filter((s) => s !== slug))}
            onTick={(slug) => setTicked(toggle(ticked, slug))}
            onPick={(skill) => {
              setSkillVerdicts({ ...skillVerdicts, [skill.slug]: skill.verdict });
              setOwnSkills([...ownSkills, skill.slug]);
            }}
            onExclude={(slug) => setExSkills(toggle(exSkills, slug))} />
        </section>
      </div>
    </Sheet>
  );
}

const NO_EXCLUDE: KitExclude = { notes: [], skills: [], actions: [], projects: [], radar: false, orders: false };

const CLAUDE_EFFORTS: { value: ClaudeEffort | ""; label: string }[] = [
  { value: "", label: "по умолчанию" },
  { value: "low", label: "low" },
  { value: "medium", label: "medium" },
  { value: "high", label: "high" },
  { value: "xhigh", label: "xhigh" },
  { value: "max", label: "max" },
];

const CODEX_EFFORTS: { value: CodexEffort | ""; label: string }[] = [
  { value: "", label: "по умолчанию" },
  { value: "minimal", label: "minimal" },
  { value: "low", label: "low" },
  { value: "medium", label: "medium" },
  { value: "high", label: "high" },
  { value: "xhigh", label: "xhigh" },
];

/** «Что изменилось»: from the version I approved (or the one before the current) to the current. */
function ChangesSince({ agent }: { agent: AgentDetailOut }) {
  const from = agent.my_approved_version ?? Math.max(agent.version - 1, 0);
  const diff = useQuery({
    queryKey: ["agents", "diff", agent.id, from, agent.version],
    queryFn: () => Agents.diff(agent.id, from, agent.version),
  });
  if (diff.isLoading) return <SkeletonList count={2} />;
  return diff.data ? <DiffView diff={diff.data} /> : <EmptyNote>Не удалось загрузить разницу версий.</EmptyNote>;
}

const WEB_TOOLS: { tool: AgentWebTool; label: string }[] = [
  { tool: "WebSearch", label: "WebSearch — поиск в сети" },
  { tool: "WebFetch", label: "WebFetch — чтение страниц" },
];

const NOTE_LEVELS = [
  { value: "none" as const, label: "нет" },
  { value: "read" as const, label: "читать" },
  { value: "append" as const, label: "дописывать" },
];
type NoteLevel = (typeof NOTE_LEVELS)[number]["value"];

/** What the unit's kit gives, each with «Убрать для этого агента» (its `kit_exclude`) or «Вернуть». */
export function KitItems({ items, excluded, onToggle }: {
  items: { key: string; label: string }[];
  excluded: string[];
  onToggle: (key: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1" data-kit-items>
      <span className="text-micro text-p-text-muted">От отдела:</span>
      {items.map((item) => {
        const out = excluded.includes(item.key);
        return (
          <div key={item.key} className="flex flex-wrap items-center justify-between gap-2">
            <span className={cn("min-w-0 flex-1 break-words text-body", out ? "text-p-text-muted line-through" : "text-p-text")}>{item.label}</span>
            <Button size="compact" variant="ghost" onClick={() => onToggle(item.key)}>{out ? "Вернуть" : "Убрать для этого агента"}</Button>
          </div>
        );
      })}
    </div>
  );
}

/** Shared notes by address: the card's own with their level, the kit's with «убрать для этого
 * агента»; «+ Добавить» finds a shared note by name. Personal and daily notes are never offered. */
function NoteGrants({ grants, titles, kitNotes, excluded, onChange, onPick, onExclude }: {
  grants: AgentNoteGrant[];
  titles: Record<string, string>;
  kitNotes: AgentGrantedNote[];
  excluded: string[];
  onChange: (g: AgentNoteGrant[]) => void;
  onPick: (note: FoundNote) => void;
  onExclude: (id: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const set = (id: string, level: NoteLevel) => {
    const rest = grants.filter((x) => x.note_id !== id);
    onChange(level === "none" ? rest : [...rest, { note_id: id, append: level === "append" }]);
  };
  const title = (id: string) => titles[id] || `Заметка ${id.slice(0, 8)}`;
  return (
    <div className="flex flex-col gap-2 pt-2" data-note-grants>
      <span className="text-meta text-p-text-muted">Заметки по адресу — без «читать всё». Только общие; личные и ежедневные не выдаются.</span>
      {grants.map((g) => (
        <div key={g.note_id} className="flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 flex-1 text-body">{title(g.note_id)}</span>
          <Segmented options={NOTE_LEVELS} value={g.append ? "append" : "read"} onChange={(v) => set(g.note_id, v)} label={`Доступ к заметке «${title(g.note_id)}»`} />
        </div>
      ))}
      {kitNotes.length > 0 && (
        <KitItems items={kitNotes.map((n) => ({ key: n.note_id, label: `${n.title || "без названия"} — ${n.append ? "дописывать" : "читать"}` }))}
          excluded={excluded} onToggle={onExclude} />
      )}
      {!grants.length && !kitNotes.length && <span className="text-meta text-p-text-muted">Заметок не выдано.</span>}
      {adding ? (
        <NoteSearch taken={[...grants.map((g) => g.note_id), ...kitNotes.map((n) => n.note_id)]}
          onPick={(note) => { onPick(note); setAdding(false); }} />
      ) : (
        <Button size="compact" variant="secondary" onClick={() => setAdding(true)}>+ Добавить заметку</Button>
      )}
    </div>
  );
}

/** The card's own skills with their verdicts (a caution skill needs the person's tick), the kit's
 * with «убрать для этого агента»; «+ Добавить» finds a registry skill by name. */
function SkillGrants({ own, verdicts, ticked, kitSkills, excluded, onRemove, onTick, onPick, onExclude }: {
  own: string[];
  verdicts: Record<string, FoundSkill["verdict"]>;
  ticked: string[];
  kitSkills: string[];
  excluded: string[];
  onRemove: (slug: string) => void;
  onTick: (slug: string) => void;
  onPick: (skill: FoundSkill) => void;
  onExclude: (slug: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="flex flex-col gap-2" data-skill-grants>
      {own.map((slug) => {
        const verdict = verdicts[slug] ?? null;
        return (
          <div key={slug} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 flex-1">
                <span className="block break-words text-body text-p-text">{slug}</span>
                <span className="block text-micro text-p-text-muted">{verdictNote(verdict)}</span>
              </span>
              <Button size="compact" variant="ghost" onClick={() => onRemove(slug)}>Убрать</Button>
            </div>
            {verdict === "caution" && (
              <Toggle label={`Разрешаю «${slug}» сам, после разбора`} on={ticked.includes(slug)} onChange={() => onTick(slug)} />
            )}
          </div>
        );
      })}
      {kitSkills.length > 0 && (
        <KitItems items={kitSkills.map((slug) => ({ key: slug, label: slug }))} excluded={excluded} onToggle={onExclude} />
      )}
      {!own.length && !kitSkills.length && <span className="text-meta text-p-text-muted">Навыков не выдано.</span>}
      {adding ? (
        <SkillSearch taken={[...own, ...kitSkills]} allowed={CARD_VERDICTS} onPick={(skill) => { onPick(skill); setAdding(false); }} />
      ) : (
        <Button size="compact" variant="secondary" onClick={() => setAdding(true)}>+ Добавить навык</Button>
      )}
    </div>
  );
}

export function Tick({ label, note, on, disabled, onChange }: { label: string; note?: string; on: boolean; disabled?: boolean; onChange: () => void }) {
  return (
    <label className={cn("flex min-h-11 items-center gap-3 rounded-compact px-1", disabled ? "opacity-55" : "press-surface")}>
      <input type="checkbox" className="h-5 w-5 shrink-0 accent-[var(--p-text)]" checked={on} disabled={disabled} onChange={onChange} />
      <span className="min-w-0 flex-1">
        <span className="block break-words text-body text-p-text">{label}</span>
        {note && <span className="block text-micro text-p-text-muted">{note}</span>}
      </span>
    </label>
  );
}

export function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: () => void }) {
  return (
    <div className="flex min-h-11 items-center justify-between gap-3">
      <span className="text-body text-p-text">{label}</span>
      <Switch on={on} label={label} onChange={onChange} />
    </div>
  );
}

/** What changed in the card, part by part: added lines and removed ones. */
export function DiffView({ diff }: { diff: AgentDiffOut }) {
  if (!diff.parts.length) return <EmptyNote>Карточка не изменилась.</EmptyNote>;
  return (
    <div className="flex flex-col gap-3">
      {diff.parts.map((part) => (
        <section key={part.part} className="space-y-1.5 rounded-card border border-p-border bg-p-surface px-4 py-3">
          <Caption>{partLabel(part.part)}</Caption>
          <ul className="space-y-0.5 font-mono text-[13px] leading-5">
            {part.lines.map((line, i) => {
              const [head, changed, tail] = markSplit(line.text, line.mark);
              return (
                <li key={i} className={cn("whitespace-pre-wrap break-words", line.sign === "+" ? "text-p-done" : "text-p-blocked line-through")}>
                  {line.sign} {head}
                  {changed && <b className="rounded-[3px] bg-p-surface-2 font-bold">{changed}</b>}
                  {tail}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** D4: what changed since the version I approved, and «Одобрить» for the current one. */
export function ApproveSheet({ agent, onClose }: { agent: AgentDetailOut; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  // Review of PR #46: never approved — the whole card, from the empty one (version 0).
  const from = agent.my_approved_version ?? 0;
  const diff = useQuery({
    queryKey: ["agents", "diff", agent.id, from, agent.version],
    queryFn: () => Agents.diff(agent.id, from, agent.version),
  });
  const approve = useMutation({
    mutationFn: () => Agents.approve(agent.id, agent.version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agents"] });
      toast.success(`Версия ${agent.version} одобрена для твоего компьютера`);
      onClose();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["agents"] });
    },
  });
  const first = agent.my_approved_version == null;
  return (
    <Sheet open onClose={onClose} title={`Одобрить версию ${agent.version}`}
      footer={
        <Button block variant="strong" loading={approve.isPending} disabled={!approvable(diff)} onClick={() => approve.mutate()}>
          Одобрить версию {agent.version}
        </Button>
      }>
      <div className="flex flex-col gap-3">
        <p className="text-body text-p-text-muted">
          {first
            ? `Ты ещё не одобрял этого агента: ниже вся карточка версии ${agent.version}. Твой компьютер запустит только одобренную тобой версию.`
            : `Ты одобрял версию ${agent.my_approved_version}. Ниже — что изменилось с неё.`}
        </p>
        {diff.isLoading ? (
          <SkeletonList count={2} />
        ) : diff.data ? (
          <DiffView diff={diff.data} />
        ) : (
          <EmptyNote>Не удалось загрузить разницу версий.</EmptyNote>
        )}
        <RuntimeGapsNote agent={agent} />
      </div>
    </Sheet>
  );
}

const RUNTIME_TITLE = { claude: "Claude Code", codex: "Codex" } as const;

/**
 * Task 0ec420a2, part 2 (Kostya, 07.10: an agent is written for Claude Code and Codex from the
 * start): what this version lacks in each runtime, seen before approving it. A warning, not a
 * refusal — the server says the same to the agent that wrote the card.
 */
export function RuntimeGapsNote({ agent }: { agent: AgentDetailOut }) {
  const check = (agent as AgentDetailOut & { runtime_check?: Record<string, string[]> }).runtime_check ?? {};
  const rows = (Object.keys(RUNTIME_TITLE) as (keyof typeof RUNTIME_TITLE)[])
    .map((runtime) => ({ name: RUNTIME_TITLE[runtime], gaps: check[runtime] ?? [] }))
    .filter((row) => row.gaps.length);
  if (!rows.length) return null;
  return (
    <section className="space-y-2 rounded-card border border-p-border bg-p-surface-2 px-4 py-3" role="note" data-runtime-gaps>
      <Caption>Запуск в обеих нейросетях</Caption>
      {rows.map((row) => (
        <div key={row.name} className="space-y-0.5">
          <p className="text-body font-semibold text-p-text">Для {row.name} в этой версии не хватает:</p>
          <ul className="space-y-0.5 text-meta text-p-text">
            {row.gaps.map((gap) => <li key={gap}>· {gap}</li>)}
          </ul>
        </div>
      ))}
      <p className="text-meta text-p-text-muted">Одобрить можно и так: шаг, которому чего-то не хватает, в этой нейросети остановится и спросит человека.</p>
    </section>
  );
}

/**
 * Spec 027, D7: the secret with a service's key. A run fetches a key only when its card ties
 * it to that service's limit; the secret itself is granted to the «Агенты» connection by hand.
 */
export function SecretPicker({ value, onChange, label = "Ключ сервиса", mineOnly = false }: {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  /** Spec 038: only the secrets this person stored — only they bind one to a tool. */
  mineOnly?: boolean;
}) {
  const resources = useQuery({ queryKey: ["resources"], queryFn: () => ResourceCards.list() });
  const secrets = useQueries({
    queries: (resources.data ?? []).map((r) => ({
      queryKey: ["secrets", r.id],
      queryFn: () => Secrets.list(r.id),
    })),
  });
  const options = secrets.flatMap((q) => q.data ?? []).filter((x) => !mineOnly || (x.my?.manage && !x.destroyed_at));
  return (
    <label className="flex flex-col gap-1 text-meta text-p-text-muted">
      {label}
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}
        className="min-h-11 rounded-lg border border-p-border-strong bg-p-surface px-3 text-body text-p-text">
        <option value="">{mineOnly ? "выбери ключ" : "без ключа"}</option>
        {options.map((x) => (
          <option key={x.id} value={x.id}>{x.resource_name ? `${x.resource_name} · ` : ""}{x.name}</option>
        ))}
      </select>
    </label>
  );
}
